import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { HollowButton, IconButton, SolidButton } from '../../components/buttons-visuals';
import ConfirmModal from '../../components/confirmModal';
import { CopyTextField, TextArea } from '../../components/inputs';
import { DOMAIN_ORGANIZATION_LOWER } from '../../constants/domainCopy';
import { useLeagueConfiguration } from '../../hooks/store';
import { LeagueService } from '../../services/leagueService';

const OrganizationInviteUrl = () => {
    const leagueConfiguration = useLeagueConfiguration();
    const [availableOrganizations, setAvailableOrganizations] = useState<Core.Models.AvailableOrganization[]>([]);
    const [names, setNames] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [deletingId, setDeletingId] = useState<string | undefined>(undefined);
    const [isDeletingAll, setIsDeletingAll] = useState(false);
    const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

    const loadAvailableOrganizations = useCallback(async () => {
        const organizations = await LeagueService.getAvailableOrganizations();
        setAvailableOrganizations(organizations);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                await loadAvailableOrganizations();
            } catch (error) {
                toast.error(Core.API.getErrorMessage(error));
            } finally {
                setIsLoading(false);
            }
        })();
    }, [loadAvailableOrganizations]);

    const addOrganizations = async () => {
        setIsAdding(true);
        try {
            const result = await LeagueService.addAvailableOrganizations({ names });
            setAvailableOrganizations(result.availableOrganizations);
            setNames('');
            if (result.addedCount === 0) {
                toast.info('Those organizations are already on the list.');
            } else {
                toast.success(
                    result.addedCount === 1 ? 'Added 1 organization.' : `Added ${result.addedCount} organizations.`
                );
            }
        } catch (error) {
            toast.error(Core.API.getErrorMessage(error));
        } finally {
            setIsAdding(false);
        }
    };

    const deleteOrganization = async (organization: Core.Models.AvailableOrganization) => {
        setDeletingId(organization.id);
        try {
            await LeagueService.deleteAvailableOrganization(organization.id);
            setAvailableOrganizations((current) => current.filter((item) => item.id !== organization.id));
        } catch (error) {
            toast.error(Core.API.getErrorMessage(error));
        } finally {
            setDeletingId(undefined);
        }
    };

    const deleteAll = async () => {
        setIsDeletingAll(true);
        try {
            await LeagueService.deleteAllAvailableOrganizations();
            setAvailableOrganizations([]);
            setConfirmDeleteAll(false);
        } finally {
            setIsDeletingAll(false);
        }
    };

    const inviteUrls =
        leagueConfiguration?.inviteCodes?.map((code: string) => ({
            code,
            url: `${leagueConfiguration.domain}/organizations/create/${code}`,
        })) ?? [];

    return (
        <section className="settings-page__invite-url global-container-centered">
            <p className="text-center">
                Sending this link to {DOMAIN_ORGANIZATION_LOWER} managers will allow them to create their{' '}
                {DOMAIN_ORGANIZATION_LOWER} in this league.
            </p>
            {inviteUrls.map(({ code, url }) => (
                <CopyTextField key={code} id={`organizationInviteLink-${code}`} label="Invite Link" value={url} />
            ))}

            <div className="settings-page__invite-organizations">
                <div className="settings-page__invite-organizations__header">
                    <h2 className="heading-2 mb0">Available organizations</h2>
                    <HollowButton
                        as="button"
                        color="destructive"
                        disabled={isLoading || availableOrganizations.length === 0 || !!deletingId}
                        onClick={() => setConfirmDeleteAll(true)}
                        pending={isDeletingAll}
                        size="small"
                    >
                        Delete all
                    </HollowButton>
                </div>
                <p>
                    People using the invite link can only register an organization from this list. If the list is empty,
                    they can enter any organization name. Paste one name per line. Copying a column from Excel works.
                </p>

                {isLoading && <p>Loading available organizations...</p>}
                {!isLoading && availableOrganizations.length === 0 && <p>No available organizations yet.</p>}
                {!isLoading && availableOrganizations.length > 0 && (
                    <ul className="settings-page__invite-organizations__list">
                        {availableOrganizations.map((organization) => (
                            <li key={organization.id} className="settings-page__invite-organizations__item">
                                <span className="settings-page__invite-organizations__name">{organization.name}</span>
                                <IconButton
                                    as="button"
                                    buttonLabel={`Remove ${organization.name}`}
                                    buttonSize="small"
                                    disabled={isAdding || isDeletingAll}
                                    onClick={() => deleteOrganization(organization)}
                                    pending={deletingId === organization.id}
                                >
                                    <FontAwesomeIcon className="color-error" icon={['fas', 'xmark']} />
                                </IconButton>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="settings-page__invite-organizations__add">
                    <TextArea
                        label="Organization names"
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setNames(event.target.value)}
                        rows={8}
                        value={names}
                    />
                    <SolidButton
                        as="button"
                        disabled={!names.trim() || isLoading || !!deletingId}
                        onClick={addOrganizations}
                        pending={isAdding}
                        size="medium"
                    >
                        Add organizations
                    </SolidButton>
                </div>
            </div>

            {confirmDeleteAll && (
                <ConfirmModal
                    confirmText="Delete all"
                    onCancel={() => setConfirmDeleteAll(false)}
                    onConfirm={deleteAll}
                    title="Delete all available organizations?"
                >
                    <p>
                        This removes every name from the registration list. Organizations that have already registered
                        stay in the league.
                    </p>
                </ConfirmModal>
            )}
        </section>
    );
};

export default OrganizationInviteUrl;
